import { captureException } from '@sentry/nextjs';
import { AxiosError } from 'axios';
import { createPost } from 'components/post/post-helpers';
import { PostType } from 'components/post/post-types';
import { htmlToText } from 'html-to-text';
import { MAX_POST_PER_PAGE } from 'lib/constants';
import { findCategory, getAllCategories, getCategory } from 'lib/db/category';
import { getCategoryPosts } from 'lib/db/post';
import { Category } from 'lib/db/types';
import { getPathOfCategoryPages } from 'lib/utils/slugs';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

import LayoutComponent from '../../../components/layout/layout-component';
import { PaginationNumber } from '../../../components/pagination/pagination-component';
import { Post as PostComponent } from '../../../components/post/post-component';
import SidebarComponent from '../../../components/sidebar/sidebar-component';
import { Typography } from '../../../components/typography/typography-component';
import { stripHtmlContent } from '../../../lib/utils/strip-html';

export default function CategoryPage({
  canonicalUrl,
  posts,
  latestPosts,
  category,
}: {
  canonicalUrl: string;
  latestPosts: PostType[];
  posts: {
    page: number;
    total: number;
    data: PostType[];
  };
  category: Category;
}) {
  const router = useRouter();

  const { page } = router.query;

  const { yoast_head_json: seo, name, description, slug } = category || {};

  const handlePageChange = function (page: number) {
    router.push(`/${slug}/page/${page}`);
  };

  const currentPage = page ? Number.parseInt(page as string) : 0;

  const SEO = {
    title: htmlToText(stripHtmlContent(seo?.title ?? '') || name || ''),
    description: htmlToText(seo?.description || description || ''),
    openGraph: {
      type: seo?.og_type || '',
      url: canonicalUrl,
      siteName: seo?.og_site_name || '',
      title: htmlToText(seo?.og_title || name || ''),
      description: htmlToText(seo?.og_description || description || ''),
      images: [
        {
          url: `${process.env.NEXT_PUBLIC_SITE_URL}/assets/earlynode-meta.png`,
          alt: name || '',
        },
      ],
    },
    canonicalUrl,
  };

  return (
    <LayoutComponent>
      <NextSeo {...SEO} />
      <div className="mx-auto mt-5 flex w-full flex-col py-5 pb-9 align-top md:container lg:mt-10 lg:flex-row">
        <div className="mx-auto w-full px-4 lg:w-[95ch]">
          <div className="mb-6">
            <Typography variant="h1" className="text-left text-[2rem]">
              {name ?? 'Archive'}
            </Typography>
            {description && (
              <Typography variant="paragraph" className="mt-4 text-left">
                {description}
              </Typography>
            )}
          </div>

          <hr className="my-4" />

          <div className="mx-4 mb-12 sm:mx-0">
            <div className="grid grid-cols-1 gap-4">
              {posts?.data?.map((post, index) => (
                <PostComponent
                  key={index}
                  variant="horizontal_categories"
                  title={post.title ?? ''}
                  description={htmlToText(
                    stripHtmlContent(post.description ?? ''),
                  )}
                  img={post.img ?? ''}
                  link={post.link}
                />
              ))}
            </div>
            <div className="mt-12 ">
              <PaginationNumber
                total={posts.total}
                page={currentPage}
                onChangePage={handlePageChange}
                hrefBuilder={(page, pageCount) =>
                  page >= 1 && page <= pageCount
                    ? `/${category.slug}/page/${page}`
                    : '#'
                }
              />
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/3">
          <SidebarComponent
            posts={latestPosts ?? []}
            categorySlug={slug ?? ''}
            postTitle={`Newest Articles of ${name}`}
          />
        </div>
      </div>
    </LayoutComponent>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const chunkSize = MAX_POST_PER_PAGE;

  const data = await getAllCategories();

  const paths = getPathOfCategoryPages({ data, chunkSize });

  return {
    paths,
    fallback: false,
  };
};

// This is a temporary fix for the error we're getting on the homepage.
// The API seems to be NOT returning an array, but an object and therefore,
// .map doesn't work.
function capitalizeFirstLetter(input: string): string {
  if (!input) return '';
  return input.charAt(0).toUpperCase() + input.slice(1);
}

const createDefaultProps = (slug: string) => ({
  latestPosts: [],
  posts: { total: 1, page: 1, data: [] },
  category: {
    yoast_head_json: '',
    name: capitalizeFirstLetter(slug),
    description: '',
    slug,
  },
  canonicalUrl: `${process.env.NEXT_PUBLIC_SITE_URL}/${slug}`,
});

export const getStaticProps: GetStaticProps = async ({ params }) => {
  // This shouldn't stay in the code base, we should remove it at some point,
  // once we figure out what's going wrong.
  const debugPostData: any = {};

  const { category, page } = params || {};

  if (typeof page !== 'string' || typeof category !== 'string') {
    return { notFound: true };
  }

  try {
    const pageNumber = Number.parseInt(page);

    const categories = await findCategory({ slug: category });

    const foundCategory = categories[0];
    const categoryId = foundCategory?.id;

    if (!categoryId) {
      captureException(
        new Error(`Category ID is undefined ${category} ${categoryId}`),
        {
          tags: {
            potential404bug: 'true',
          },
        },
      );

      return { notFound: true };
    }

    const posts = await getCategoryPosts({
      categoryId: foundCategory.id,
      limit: 5,
      page: pageNumber,
    });
    debugPostData['posts'] = posts;

    const latestPosts = await getCategoryPosts({
      categoryId: foundCategory.id,
      limit: 5,
      page: 1,
    });
    debugPostData['latestPosts'] = latestPosts;

    const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/${category}/page/${page}`;

    return {
      props: {
        canonicalUrl,
        latestPosts: latestPosts?.data?.map(createPost) ?? [],
        posts: {
          total: posts.total,
          page: posts.page,
          data: posts?.data?.map(createPost) ?? [],
        },
        category: {
          yoast_head_json: foundCategory.yoast_head_json,
          name: foundCategory.name,
          description: foundCategory.description,
          slug: foundCategory.slug,
        },
      },
      revalidate: 600,
    };
  } catch (error) {
    captureException(error, {
      extra: {
        path: `getStaticProps: /${category}`,
        debugPostData,
        apiError: error instanceof AxiosError ? error?.response?.data : false,
        tags: {
          potential404bug: 'true',
        },
      },
    });
    return { props: createDefaultProps(category) };
  }
};
